/**
 * DomeBackground — R3F component that loads and renders a dome GLB mesh
 * with a projected gradient texture as the studio background.
 *
 * The dome is viewed from the INSIDE (camera sits at origin). The texture
 * uses equirectangular mapping for seamless projection onto the dome surface.
 * Emissive channel ensures the dome stays visible even with very dark tints.
 * Tint changes animate with a smooth color lerp (no scene rebuild).
 *
 * Architecture note — Suspense-safe loading:
 *   Both the GLB mesh (GLTFLoader) and the texture (TextureLoader) are loaded
 *   via useLoader so they participate in R3F's Suspense cache.  The parent
 *   <Suspense fallback={null}> in EnvironmentComponent therefore waits for
 *   BOTH assets before rendering anything — eliminating the "dome appears
 *   gray for a moment" flash.
 *   Because React's hook rules forbid conditional hook calls, the optional
 *   textureUrl is handled by having two inner components (Textured / Plain)
 *   each of which calls useLoader unconditionally, routed by the public
 *   DomeBackground wrapper.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import { useMemo, useEffect, useRef } from 'react';
import { useLoader, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DomeBackgroundProps {
  /** URL to the dome GLB mesh. */
  meshUrl: string;
  /** Optional URL to the dome texture (projected via equirectangular mapping). */
  textureUrl?: string;
  /** Tint color (hex). Drives both diffuse color and emissive glow. */
  tint?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DOME_SCALE = 10;
const DEFAULT_TINT = '#C5C5C5';
/**
 * Emissive intensity range — dark tints need more intensity to stay visible,
 * bright/light tints need less (otherwise they blow out).
 * Derived automatically from the tint's perceptual luminance every frame.
 */
const MIN_EMISSIVE_INTENSITY = 0;  // used for near-white tints
const MAX_EMISSIVE_INTENSITY = 5.0;  // used for near-black tints
/** Color lerp speed per second (0–1 range, 4 = ~250ms perceived). */
const COLOR_LERP_SPEED = 4;

/**
 * Power-curve exponent for the emissive intensity mapping.
 * γ > 1 concentrates the intensity boost in the near-black range.
 */
const EMISSIVE_GAMMA = 2.5;

/**
 * Saturation penalty weight (0–1).
 * Saturated colours (red, blue) should not glow artificially — only
 * dark/neutral tints like black or dark-grey need the emissive lift.
 * At 0.7 a fully-saturated hue receives only 30 % of the base boost.
 */
const SATURATION_PENALTY = 0.7;

/**
 * Perceptual luminance (ITU-R BT.709) of a THREE.Color.
 * Returns 0 for black, 1 for white.
 */
function luminance(c: THREE.Color): number {
  return c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
}

/** Map luminance [0=dark, 1=bright] to emissive intensity [max, min] via power curve,
 * with a saturation penalty so that saturated hues (red, blue) are not over-boosted.
 *
 * Examples (MAX=5, GAMMA=2.5, PENALTY=0.7):
 *   black        sat=0, lum=0.00  → factor 1.00 × 1.00 → intensity 5.0  ✓ full boost
 *   dark grey    sat=0, lum=0.05  → factor 0.88 × 1.00 → intensity 4.4  ✓
 *   red #ff0000  sat=1, lum=0.21  → factor 0.55 × 0.30 → intensity 0.8  ✓ not glowing
 *   blue #0000ff sat=1, lum=0.07  → factor 0.83 × 0.30 → intensity 1.2  ✓ subdued
 *   white        sat=0, lum=1.00  → factor 0.00 × 1.00 → intensity 0.0  ✓ no boost
 */
function emissiveIntensityFromColor(c: THREE.Color): number {
  const lum = luminance(c);
  const maxCh = Math.max(c.r, c.g, c.b);
  const minCh = Math.min(c.r, c.g, c.b);
  // HSV saturation: 0 = neutral grey/black, 1 = fully saturated hue
  const sat = maxCh > 0 ? (maxCh - minCh) / maxCh : 0;
  const darknessFactor = Math.pow(1 - lum, EMISSIVE_GAMMA);
  const saturationMultiplier = 1 - sat * SATURATION_PENALTY;
  return MIN_EMISSIVE_INTENSITY +
    (MAX_EMISSIVE_INTENSITY - MIN_EMISSIVE_INTENSITY) * darknessFactor * saturationMultiplier;
}

// ---------------------------------------------------------------------------
// Core render logic — shared between textured and plain variants
// ---------------------------------------------------------------------------

interface DomeBhCoreProps {
  /** Pre-parsed GLTF from useLoader. */
  gltf: GLTF;
  /** Already-configured texture (or null for no texture). */
  texture: THREE.Texture | null;
  tint?: string;
}

/**
 * Handles clone + material setup, tint lerp animation, and render output.
 * Called by both DomeBackgroundTextured and DomeBackgroundPlain after their
 * respective useLoader calls have resolved.
 */
function DomeBhCore({ gltf, texture, tint }: DomeBhCoreProps) {
  const matRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const targetColor = useRef(new THREE.Color(tint ?? DEFAULT_TINT));
  const invalidate = useThree((s) => s.invalidate);

  // Update target color when tint prop changes (no mesh rebuild)
  // invalidate() is critical: kicks the demand-mode render loop so the first
  // useFrame lerp frame fires immediately — without it the tint appears to
  // jump on the next unrelated interaction instead of animating smoothly.
  useEffect(() => {
    targetColor.current.set(tint ?? DEFAULT_TINT);
    invalidate(); // bootstrap: schedule first lerp frame
  }, [tint, invalidate]);

  // Clone the GLTF scene once and apply material
  const domeMesh = useMemo(() => {
    const clone = gltf.scene.clone(true);
    const initialColor = new THREE.Color(tint ?? DEFAULT_TINT);

    const mat = new THREE.MeshStandardMaterial({
      map: texture ?? undefined,
      color: initialColor,
      metalness: 0.2,
      roughness: 1.0,
      emissive: initialColor.clone(),
      emissiveMap: texture ?? undefined,
      emissiveIntensity: emissiveIntensityFromColor(initialColor),
      side: THREE.FrontSide,
      depthWrite: false,
      toneMapped: false,
    });
    matRef.current = mat;

    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        (child as THREE.Mesh).material = mat;
        child.renderOrder = -1000;
        child.frustumCulled = false;
      }
    });

    clone.scale.setScalar(DOME_SCALE);
    return clone;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gltf, texture]); // tint intentionally excluded — animated via useFrame

  // Smooth color lerp every frame
  // invalidate() keeps the demand-mode loop alive until the lerp converges.
  useFrame((_, delta) => {
    const mat = matRef.current;
    if (!mat) return;
    const t = Math.min(delta * COLOR_LERP_SPEED, 1);
    mat.color.lerp(targetColor.current, t);
    mat.emissive.lerp(targetColor.current, t);
    // Auto-adjust emissive intensity from current (lerping) color's luminance.
    // As the tint transitions from dark to light the intensity falls smoothly,
    // and vice-versa — no separate lerp needed, it rides the color lerp for free.
    mat.emissiveIntensity = emissiveIntensityFromColor(mat.color);
    // Keep rendering while colors haven't converged (threshold: <1/255 per channel)
    if (!mat.color.equals(targetColor.current)) invalidate();
  });

  // Cleanup on unmount — only dispose resources WE created, not useLoader cache data
  useEffect(() => {
    return () => {
      matRef.current?.dispose();
      matRef.current = null;
    };
  }, []);

  return <primitive object={domeMesh} />;
}

// ---------------------------------------------------------------------------
// Textured variant — loads BOTH mesh and texture via useLoader (Suspense-safe)
// ---------------------------------------------------------------------------

/**
 * Loads the dome GLB AND texture through R3F's useLoader (Suspense cache).
 * The outer <Suspense fallback={null}> in EnvironmentComponent therefore
 * waits for both assets before rendering anything — no gray flash.
 */
function DomeBackgroundTextured({ meshUrl, textureUrl, tint }: { meshUrl: string; textureUrl: string; tint?: string }) {
  const gltf    = useLoader(GLTFLoader, meshUrl);
  const rawTex  = useLoader(THREE.TextureLoader, textureUrl) as THREE.Texture;

  // Apply equirectangular projection settings on the useLoader-managed texture.
  // useMemo rather than useEffect so settings are in place before DomeBhCore
  // creates the material (avoids a one-frame render with wrong mapping).
  const texture = useMemo(() => {
    // eslint-disable-next-line react-hooks/immutability -- Three.js texture requires direct mutation of mapping/wrap/colorSpace after load
    rawTex.mapping    = THREE.EquirectangularReflectionMapping;
    // eslint-disable-next-line react-hooks/immutability
    rawTex.wrapS      = THREE.RepeatWrapping;
    // eslint-disable-next-line react-hooks/immutability
    rawTex.wrapT      = THREE.RepeatWrapping;
    rawTex.repeat.set(1, 1);
    rawTex.offset.set(0.0, 0.02);
    // eslint-disable-next-line react-hooks/immutability
    rawTex.rotation   = Math.PI;
    rawTex.center.set(0.5, 0.5);
    // eslint-disable-next-line react-hooks/immutability
    rawTex.colorSpace = THREE.SRGBColorSpace;
    // eslint-disable-next-line react-hooks/immutability
    rawTex.needsUpdate = true;
    return rawTex;
  }, [rawTex]);

  return <DomeBhCore gltf={gltf} texture={texture} tint={tint} />;
}

// ---------------------------------------------------------------------------
// Plain variant — mesh only (no texture)
// ---------------------------------------------------------------------------

function DomeBackgroundPlain({ meshUrl, tint }: { meshUrl: string; tint?: string }) {
  const gltf = useLoader(GLTFLoader, meshUrl);
  return <DomeBhCore gltf={gltf} texture={null} tint={tint} />;
}

// ---------------------------------------------------------------------------
// Public component — delegates to Textured or Plain
// ---------------------------------------------------------------------------

/**
 * DomeBackground — public API.
 *
 * Delegates to DomeBackgroundTextured or DomeBackgroundPlain so that each
 * inner component always calls useLoader unconditionally (React hook rule).
 * Both variants suspend via R3F's Suspense cache; the parent EnvironmentComponent
 * wraps this in <Suspense fallback={null}> to hide the dome until fully ready.
 */
export function DomeBackground({ meshUrl, textureUrl, tint }: DomeBackgroundProps) {
  if (textureUrl) {
    return <DomeBackgroundTextured meshUrl={meshUrl} textureUrl={textureUrl} tint={tint} />;
  }
  return <DomeBackgroundPlain meshUrl={meshUrl} tint={tint} />;
}
